import _ from 'lodash';

import { AiProcessAwaitingStatuses } from 'constants/aiProcessRuns';
import { MILLISECONDS_PER_SECOND } from 'constants/date';

import { formatDurationSeconds } from '../../../AiActivity.utils';

import {
  IDLE_WAIT_AXIS_PADDING,
  OVERLAP_TOLERANCE,
  PERCENT_MAX,
  TIMELINE_SUMMARY_SEPARATOR,
} from './RunTimeline.constants';

const toMs = value => (_.isNil(value) ? null : new Date(value).getTime());

const getStepEndMs = (step, nowMs) => toMs(step.completedAt) ?? nowMs;

const getPeakConcurrency = intervals => {
  const edges = _.sortBy(
    _.flatMap(intervals, ({ startMs, endMs }) => [
      { atMs: startMs, delta: 1 },
      { atMs: endMs, delta: -1 },
    ]),
    // Close before open at the same instant, so back-to-back steps are not counted as overlapping.
    ['atMs', 'delta'],
  );

  return _.reduce(
    edges,
    ({ current, peak }, { delta }) => ({
      current: current + delta,
      peak: Math.max(peak, current + delta),
    }),
    { current: 0, peak: 0 },
  ).peak;
};

export const buildTimeline = ({ steps, runStartedAt, runCompletedAt, nowMs = Date.now() }) => {
  const startedSteps = _.filter(steps, step => !_.isNil(step.startedAt));

  if (startedSteps.length === 0) {
    return null;
  }

  const startMs = Math.min(
    toMs(runStartedAt) ?? Infinity,
    ..._.map(startedSteps, s => toMs(s.startedAt)),
  );
  const isLive = _.isNil(runCompletedAt);
  const endMs = Math.max(
    startMs + MILLISECONDS_PER_SECOND,
    toMs(runCompletedAt) ?? nowMs,
    ..._.map(startedSteps, step => getStepEndMs(step, nowMs)),
  );

  // A step waiting on a person is idle until someone answers, so its open-ended
  // bar is shown but never counted as work.
  const [waitingSteps, workingSteps] = _.partition(startedSteps, step =>
    AiProcessAwaitingStatuses.has(step.status),
  );
  const workIntervals = _.map(workingSteps, step => ({
    startMs: toMs(step.startedAt),
    endMs: Math.max(toMs(step.startedAt), getStepEndMs(step, nowMs)),
  }));

  // While the run sits idle on a person, drawing the axis out to "now" would
  // squeeze hours of waiting against minutes of work; end it just after the last
  // activity instead and draw the waiting steps as open-ended.
  const isIdleWait =
    isLive && waitingSteps.length > 0 && _.every(workingSteps, step => !_.isNil(step.completedAt));
  const lastActivityMs = Math.max(
    startMs + MILLISECONDS_PER_SECOND,
    ..._.map(workIntervals, 'endMs'),
    ..._.map(waitingSteps, step => toMs(step.startedAt)),
  );
  const axisEndMs = isIdleWait
    ? lastActivityMs + (lastActivityMs - startMs) * IDLE_WAIT_AXIS_PADDING
    : endMs;
  const axisSpanMs = axisEndMs - startMs;
  const toPercent = ms => ((ms - startMs) / axisSpanMs) * PERCENT_MAX;

  const rows = _.map(steps, step => {
    if (_.isNil(step.startedAt)) {
      return { id: step.id, name: step.name, status: step.status, leftPercent: 0, widthPercent: 0 };
    }

    const leftPercent = toPercent(toMs(step.startedAt));
    const isOpenEnded = isIdleWait && AiProcessAwaitingStatuses.has(step.status);

    return {
      id: step.id,
      name: step.name,
      status: step.status,
      leftPercent,
      widthPercent: isOpenEnded
        ? PERCENT_MAX - leftPercent
        : toPercent(getStepEndMs(step, nowMs)) - leftPercent,
      isOpenEnded,
    };
  });

  const workMs = _.sumBy(workIntervals, ({ startMs: from, endMs: to }) => to - from);
  const workSpanMs = _.isEmpty(workIntervals)
    ? 0
    : _.maxBy(workIntervals, 'endMs').endMs - _.minBy(workIntervals, 'startMs').startMs;
  const hasOverlap = workSpanMs > 0 && workMs > workSpanMs * OVERLAP_TOLERANCE;

  return {
    rows,
    nowPercent: isLive && !isIdleWait ? toPercent(nowMs) : null,
    elapsedSeconds: Math.round((endMs - startMs) / MILLISECONDS_PER_SECOND),
    workSeconds: Math.round(workMs / MILLISECONDS_PER_SECOND),
    peakConcurrency: getPeakConcurrency(workIntervals),
    speedup: hasOverlap ? workMs / workSpanMs : null,
    waitingCount: waitingSteps.length,
  };
};

export const formatTimelineSummary = ({ elapsedSeconds, peakConcurrency, speedup, waitingCount }) =>
  _.compact([
    `Elapsed ${formatDurationSeconds(elapsedSeconds)}`,
    peakConcurrency > 1 && `up to ${peakConcurrency} steps at once`,
    !_.isNil(speedup) && `${speedup.toFixed(1)}× faster than one at a time`,
    waitingCount > 0 &&
      `${waitingCount} ${waitingCount === 1 ? 'step' : 'steps'} waiting for your input`,
  ]).join(TIMELINE_SUMMARY_SEPARATOR);
